import { useMemo, useCallback } from 'react';
import { 
  filterMessagesToPins, 
  clusterPins, 
  DEFAULT_PIN_CONFIG 
} from '../utils/pinTypes';

/**
 * Hook for processing chat messages into map pins
 * @param {Array} allMessages - Array of chat messages (from usePaginatedMessages)
 * @param {Object} config - Pin configuration options
 * @returns {Object} Pin data and loading states
 */
const useMessagePins = (allMessages = [], config = {}) => {
  // Stable configuration to prevent infinite re-renders
  const pinConfig = useMemo(() => {
    // Deep merge to ensure config stability
    const merged = {
      enabledTypes: config.enabledTypes || DEFAULT_PIN_CONFIG.enabledTypes,
      clustering: config.clustering !== undefined ? config.clustering : DEFAULT_PIN_CONFIG.clustering,
      clusterDistance: config.clusterDistance || DEFAULT_PIN_CONFIG.clusterDistance,
      maxPins: config.maxPins !== undefined ? config.maxPins : DEFAULT_PIN_CONFIG.maxPins,
      minZoomLevel: config.minZoomLevel || DEFAULT_PIN_CONFIG.minZoomLevel,
      showInSidebar: config.showInSidebar !== undefined ? config.showInSidebar : DEFAULT_PIN_CONFIG.showInSidebar
    };
    return merged;
  }, [
    config.enabledTypes,
    config.clustering,
    config.clusterDistance,
    config.maxPins,
    config.minZoomLevel,
    config.showInSidebar
  ]);

  // Messages are now passed as parameter instead of fetched directly

  // Filter messages to pins and apply clustering with error handling
  const { messagePins, clusteredPins, pinStats } = useMemo(() => {
    try {
      if (!allMessages || allMessages.length === 0) {
        return {
          messagePins: [],
          clusteredPins: [],
          pinStats: { total: 0, byType: {} }
        };
      }

      // Filter messages to pins based on enabled types
      const pins = filterMessagesToPins(allMessages, pinConfig.enabledTypes);
      
      // Limit number of pins if configured
      const limitedPins = pinConfig.maxPins > 0 
        ? pins.slice(0, pinConfig.maxPins)
        : pins;

      // Apply clustering if enabled
      const clustered = pinConfig.clustering 
        ? clusterPins(limitedPins, pinConfig.clusterDistance)
        : limitedPins.map(pin => ({
            id: `single-${pin.messageId}`,
            type: 'single',
            centerPin: pin,
            pins: [pin],
            location: pin.location,
            count: 1
          }));

      // Calculate statistics
      const stats = {
        total: limitedPins.length,
        byType: limitedPins.reduce((acc, pin) => {
          acc[pin.type] = (acc[pin.type] || 0) + 1;
          return acc;
        }, {})
      };

      return {
        messagePins: limitedPins,
        clusteredPins: clustered,
        pinStats: stats
      };
    } catch (error) {
      console.error('Error processing message pins:', error);
      return {
        messagePins: [],
        clusteredPins: [],
        pinStats: { total: 0, byType: {} }
      };
    }
  }, [allMessages, pinConfig]);

  // Memoized helper functions to prevent re-creation on every render
  const getPinsByType = useCallback((type) => {
    return messagePins.filter(pin => pin.type === type);
  }, [messagePins]);

  const getPinById = useCallback((messageId) => {
    return messagePins.find(pin => pin.messageId === messageId);
  }, [messagePins]);

  const getClusterById = useCallback((clusterId) => {
    return clusteredPins.find(cluster => cluster.id === clusterId);
  }, [clusteredPins]);

  // Check if pins should be shown based on configuration
  const shouldShowPins = useMemo(() => {
    return pinConfig.showInSidebar && messagePins.length > 0;
  }, [pinConfig.showInSidebar, messagePins.length]);

  return {
    // Pin data
    messagePins,           // Raw pins array
    clusteredPins,         // Clustered pins for map rendering
    pinStats,              // Statistics about pins
    
    // Configuration
    config: pinConfig,
    
    // Helper functions
    getPinsByType,
    getPinById,
    getClusterById,
    shouldShowPins,
    
    // Computed properties
    hasPhotoPins: pinStats.byType.PHOTO > 0,
    hasAudioPins: pinStats.byType.AUDIO > 0,
    hasDiscoveryPins: pinStats.byType.DISCOVERY > 0,
    hasMilestonePins: pinStats.byType.MILESTONE > 0
  };
};

export default useMessagePins;
